import { Component, ComponentInterface, Element, h, Host, JSX, Prop, Watch } from '@stencil/core';
import { transformTagNames } from '../../global/transform-tag-name';
import { WorkbenchTheme, getDataOnFromTheme } from '../../shared/themes';
import { ICON_ERROR, ICON_SUCCESS } from './badge.constants';
import { BadgeVariant } from './badge.types';
import { getComputedBackgroundColor } from '../../shared/get-computed-background-color';

/**
 * The Status Badge provides additional information to the user, regarding the status of a process or event.
 *
 * @slot - Custom icon placed within the element
 */
@Component({
  tag: 'wb-badge',
  styleUrl: 'badge.scss',
  shadow: true,
})
export class BadgeComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Color variant of the badge */
  @Prop({ reflect: true })
  public variant?: BadgeVariant;

  /** Optional border of the badge */
  @Prop({ reflect: true })
  public hasBorder: boolean = false;

  @Watch('variant')
  public onVariantChange(): void {
    this.setIcon();
  }

  private icon: string;
  private backgroundColor: string;

  public connectedCallback(): void {
    this.setIcon();
    this.setBackgroundColor();
  }

  private TagNames = transformTagNames(this, 'wb-badge', ['wb-icon']);

  private setIcon = (): void => {
    if (this.variant === 'error') {
      this.icon = ICON_ERROR;
    } else {
      this.icon = ICON_SUCCESS;
    }
  };

  private setBackgroundColor = (): void => {
    if (!this.hasBorder) {
      return;
    }
    this.backgroundColor = getComputedBackgroundColor(this.el);
  };

  public render(): JSX.Element {
    const { WbIcon } = this.TagNames;
    const isWarning = this.variant === 'warning';

    return (
      <Host class="wb-badge" style={this.hasBorder ? { '--badge-border-ring-color': this.backgroundColor } : undefined} data-on={getDataOnFromTheme(this.el, this.theme)}>
        {isWarning ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 16">
            <g>
              <path class="triangle" d="M1.96624 15L16.0338 15C16.7717 15 17.2365 14.2292 16.8747 13.6054L9.84092 1.47702C9.47206 0.840992 8.52793 0.840994 8.15907 1.47702L1.12531 13.6054C0.763535 14.2292 1.22831 15 1.96624 15Z" />
              <path d="M8.17209 4H9.83876V10.0872H8.17209V4Z" />
              <path d="M9 14C9.69036 14 10.25 13.4404 10.25 12.75C10.25 12.0596 9.69036 11.5 9 11.5C8.30964 11.5 7.75 12.0596 7.75 12.75C7.75 13.4404 8.30964 14 9 14Z" />
              <path class="triangle-border" d="M1.96623 16L16.0338 16C17.4887 16 18.5148 14.4401 17.7397 13.1037L10.706 0.975338C9.95178 -0.325117 8.04821 -0.325109 7.29402 0.97534L0.260259 13.1037C-0.514769 14.4401 0.51132 16 1.96623 16ZM16.0338 15L1.96624 15C1.22831 15 0.763535 14.2292 1.12531 13.6054L8.15907 1.47702C8.52793 0.840994 9.47206 0.840992 9.84092 1.47702L16.8747 13.6054C17.2365 14.2292 16.7717 15 16.0338 15Z" />
            </g>
          </svg>
        ) : this.variant ? (
          <WbIcon name={this.icon} />
        ) : (
          <slot />
        )}
      </Host>
    );
  }
}
